import React, { useState, useEffect, useRef, useContext } from "react"
import { SessionContext } from "context/sessionContext"
import { useNavigate, Link } from "react-router-dom"
import Breadcrumb from "components/Common/Breadcrumb"
import Swal from "sweetalert2"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Input,
} from "reactstrap"
import { examMasterApi } from "apis/ExamMasterApi"

// ── Defined OUTSIDE ExamMain so React never remounts it on parent re-render ──
const StatusSelect = ({ isActive, isToggling, onChange, disabled = false }) => {
  if (isToggling) {
    return <Spinner size="sm" color="primary" />
  }

  return (
    <select
      value={isActive ? "active" : "inactive"}
      disabled={disabled}
      onChange={e => onChange(e.target.value === "active")}
      style={{
        border: "1px solid",
        borderColor: isActive ? "#2e7d32" : "#ced4da",
        borderRadius: "4px",
        padding: "3px 8px",
        fontSize: "12px",
        fontWeight: 600,
        color: isActive ? "#2e7d32" : "#6c757d",
        background: disabled ? "#e9ecef" : isActive ? "#e8f5e9" : "#f8f9fa",
        cursor: disabled ? "not-allowed" : "pointer",
        outline: "none",
      }}
    >
      <option value="active">Active</option>
      <option value="inactive">Inactive</option>
    </select>
  )
}

const ExamMain = () => {
  document.title = "Exam Management"
  const navigate = useNavigate()
  const isCoAssessment = (subjects) => subjects && subjects.some(
    s => s.subject_code === "" || String(s.subject_code).startsWith("CO")
  )
  const [examList, setExamList] = useState([])
  const [loading, setLoading] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [expandedId, setExpandedId] = useState(null)

  // Track toggling per assessment or subject: key = `assessment-{id}` or `subject-{id}`
  const [togglingKey, setTogglingKey] = useState(null)

  // ── Per-assessment collapse (subjects table) ───────────────────────────
  const [collapsedAssessments, setCollapsedAssessments] = useState(new Set())

  const toggleAssessmentCollapse = assessmentId => {
    setCollapsedAssessments(prev => {
      const next = new Set(prev)
      next.has(assessmentId)
        ? next.delete(assessmentId)
        : next.add(assessmentId)
      return next
    })
  }

  // ── Pagination ─────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // ── Search ──────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")

  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      fetchExams(1, "")
      return
    }
    const delayDebounceFn = setTimeout(() => {
      setAppliedSearch(searchQuery)
      fetchExams(1, searchQuery)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [searchQuery])

  const { Session } = useContext(SessionContext)
  const activeSessionId = Session || sessionStorage.getItem("SessionId")

  useEffect(() => {
    if (activeSessionId && !isFirstRender.current) {
      fetchExams(1, appliedSearch)
    }
  }, [activeSessionId])

  const fetchExams = async (page = 1, search = "") => {
    setLoading(true)
    try {
      const res = await examMasterApi.getExam(page, search, activeSessionId ? parseInt(activeSessionId, 10) : null)
      const data = res?.data ?? res
      if (data?.status === 200 && Array.isArray(data?.exams)) {
        setExamList(data.exams)
        setTotalPages(data.total_pages ?? 1)
        setTotalCount(data.total_count ?? data.exams.length)
        setCurrentPage(data.page_number ?? page)
      } else {
        setExamList([])
      }
    } catch (error) {
      console.error("Failed to fetch exams:", error)
      setExamList([])
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => {
    setAppliedSearch(searchQuery)
    fetchExams(1, searchQuery)
  }

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") handleSearch()
  }

  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    setExpandedId(null)
    fetchExams(page, appliedSearch)
  }

  const handleDelete = async examId => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    })

    if (!result.isConfirmed) return

    setDeletingId(examId)
    try {
      const res = await examMasterApi.deleteExam({ exam_id: examId })
      const data = res?.data ?? res
      if (data?.status === 200) {
        if (expandedId === examId) setExpandedId(null)
        Swal.fire({
          title: "Deleted!",
          text: data?.msg || "Exam has been deleted successfully.",
          icon: "success",
          confirmButtonColor: "#556ee6",
        })
        const newPage =
          examList.length === 1 && currentPage > 1
            ? currentPage - 1
            : currentPage
        fetchExams(newPage, appliedSearch)
      } else {
        Swal.fire({
          title: "Error",
          text: data?.msg || "Failed to delete exam. Please try again.",
          icon: "error",
          confirmButtonColor: "#556ee6",
        })
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text:
          error.response?.data?.msg ||
          "An error occurred while deleting the exam.",
        icon: "error",
        confirmButtonColor: "#556ee6",
      })
    } finally {
      setDeletingId(null)
    }
  }

  const toggleExpand = id => {
    setExpandedId(prev => (prev === id ? null : id))
  }

  // ── Toggle assessment status ───────────────────────────────────────────
  const handleToggleAssessment = async (examId, assessment, newStatus) => {
    const key = `assessment-${assessment.id}`
    setTogglingKey(key)
    try {
      const res = await examMasterApi.updateExamStatus({
        assessment_id: assessment.id,
        is_active: newStatus,
      })
      const data = res?.data ?? res
      if (data?.status === 200) {
        setExamList(prev =>
          prev.map(exam =>
            exam.id !== examId
              ? exam
              : {
                  ...exam,
                  assessments: exam.assessments.map(a => {
                    if (a.id !== assessment.id) {
                      return a
                    }

                    return {
                      ...a,
                      is_active: newStatus,

                      subjects: (a.subjects ?? []).map(s => ({
                        ...s,
                        is_active: newStatus,
                      })),

                      sub_assessments: (a.sub_assessments ?? []).map(sa => ({
                        ...sa,
                        subjects: (sa.subjects ?? []).map(s => ({
                          ...s,
                          is_active: newStatus,
                        })),
                      })),
                    }
                  }),
                }
          )
        )
      } else {
        Swal.fire({
          title: "Error",
          text: data?.msg || "Failed to update assessment status.",
          icon: "error",
          confirmButtonColor: "#556ee6",
        })
      }
    } catch (err) {
      console.error("updateAssessmentStatus error:", err)
      Swal.fire({
        title: "Error",
        text: err.response?.data?.msg || "An error occurred.",
        icon: "error",
        confirmButtonColor: "#556ee6",
      })
    } finally {
      setTogglingKey(null)
    }
  }

  // ── Toggle subject status ──────────────────────────────────────────────
  const handleToggleSubject = async (
    examId,
    assessmentId,
    subAssessmentId,
    subject,
    newStatus
  ) => {
    const key = `subject-${subject.id}`
    setTogglingKey(key)
    try {
      const res = await examMasterApi.updateExamStatus({
        subject_mark_id: subject.id,
        is_active: newStatus,
      })
      const data = res?.data ?? res
      if (data?.status === 200) {
        setExamList(prev =>
          prev.map(exam => {
            if (exam.id !== examId) return exam
            return {
              ...exam,
              assessments: exam.assessments.map(a => {
                if (a.id !== assessmentId) return a

                const updatedSubjects =
                  subAssessmentId == null
                    ? (a.subjects ?? []).map(s =>
                        s.id === subject.id ? { ...s, is_active: newStatus } : s
                      )
                    : a.subjects

                const updatedSubAssessments = (a.sub_assessments ?? []).map(
                  sa => {
                    if (sa.id !== subAssessmentId) return sa
                    return {
                      ...sa,
                      subjects: (sa.subjects ?? []).map(s =>
                        s.id === subject.id ? { ...s, is_active: newStatus } : s
                      ),
                    }
                  }
                )

                return {
                  ...a,
                  subjects: updatedSubjects,
                  sub_assessments: updatedSubAssessments,
                }
              }),
            }
          })
        )
      } else {
        Swal.fire({
          title: "Error",
          text: data?.msg || "Failed to update subject status.",
          icon: "error",
          confirmButtonColor: "#556ee6",
        })
      }
    } catch (err) {
      console.error("updateSubjectStatus error:", err)
      Swal.fire({
        title: "Error",
        text: err.response?.data?.msg || "An error occurred.",
        icon: "error",
        confirmButtonColor: "#556ee6",
      })
    } finally {
      setTogglingKey(null)
    }
  }

  const formatDate = dateStr => {
    if (!dateStr) return "—"
    const d = new Date(dateStr)
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
  }

  const renderClassBadges = st_class => {
    if (!st_class) return null
    const raw = Array.isArray(st_class) ? st_class : []
    const uniqueClasses = [
      ...new Set(
        raw
          .map(s => String(s).trim().split(" ")[0].toUpperCase())
          .filter(Boolean)
      ),
    ]
    return uniqueClasses.map(cls => (
      <Badge key={cls} color="info" className="me-1">
        {cls}
      </Badge>
    ))
  }

  // ── Pagination renderer ────────────────────────────────────────────────
  const renderPagination = () => {
    if (totalPages <= 1) return null
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing page <strong>{currentPage}</strong> of{" "}
          <strong>{totalPages}</strong>&nbsp;|&nbsp;Total:{" "}
          <strong>{totalCount}</strong> exams
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>
              «
            </button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage - 1)}
            >
              ‹
            </button>
          </li>
          {pages.map(p => (
            <li
              key={p}
              className={`page-item ${p === currentPage ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => handlePageChange(p)}>
                {p}
              </button>
            </li>
          ))}
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(currentPage + 1)}
            >
              ›
            </button>
          </li>
          <li
            className={`page-item ${
              currentPage === totalPages ? "disabled" : ""
            }`}
          >
            <button
              className="page-link"
              onClick={() => handlePageChange(totalPages)}
            >
              »
            </button>
          </li>
        </ul>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid={true}>
            <Breadcrumb title="Examination" breadcrumbItem="Exam Master" />

            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody className="border-bottom">
                    <div className="d-flex align-items-center gap-2">
                      <div className="d-flex gap-2 flex-grow-1">
                        <div style={{ position: "relative", width: "100%", maxWidth: "520px" }}>
                          <Input
                            type="text"
                            placeholder="Search by exam name..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            style={{ fontSize: "13px", paddingRight: "30px" }}
                          />
                          {searchQuery && (
                            <button
                              type="button"
                              onClick={() => {
                                setSearchQuery("")
                                setAppliedSearch("")
                                fetchExams(1, "")
                              }}
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                border: "none",
                                background: "none",
                                cursor: "pointer",
                                fontSize: "12px",
                                padding: 0,
                                color: "#6c757d",
                                zIndex: 5,
                              }}
                            >
                              ❌
                            </button>
                          )}
                        </div>
                        <Button
                          color="primary"
                          onClick={handleSearch}
                          disabled={loading}
                          style={{ whiteSpace: "nowrap" }}
                        >
                          <i className="bx bx-search me-1" />
                          Search
                        </Button>
                      </div>
                      <div className="flex-shrink-0">
                        <Button
                          color="primary"
                          className="waves-effect waves-light"
                          onClick={() => navigate("/exam-create")}
                        >
                          + Create Exam
                        </Button>
                      </div>
                    </div>
                  </CardBody>

                  <div
                    style={{
                      background: "#fffbe6",
                      borderLeft: "4px solid #faad14",
                      padding: "8px 16px",
                      margin: "0 16px",
                      borderRadius: "4px",
                      fontSize: "13px",
                      color: "#7c5a00",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "8px",
                    }}
                  >
                    <i
                      className="bx bx-info-circle"
                      style={{ fontSize: "16px", marginTop: "1px", flexShrink: 0 }}
                    />
                    <span>
                      <strong>Note:</strong> Inactive Assessments/Subjects remain
                      fully accessible to Admin for mark entry and reports
                      generation — only Teacher mark-entry access is restricted.
                    </span>
                  </div>

                  <CardBody>
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading exams...</p>
                      </div>
                    ) : examList.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i
                          className="bx bx-file"
                          style={{ fontSize: "40px" }}
                        />
                        <p className="mt-2">No exams found.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-bordered align-middle">
                          <thead className="table-light">
                            <tr>
                              <th width="60">Sr.No</th>
                              <th>Exam Name</th>
                              <th>Description</th>
                              <th>Assessments</th>
                              <th>Created At</th>
                              <th width="120">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {examList.map((exam, index) => (
                              <React.Fragment key={exam.id}>
                                <tr>
                                  <td>{(currentPage - 1) * 10 + index + 1}</td>
                                  <td>
                                    <strong>{exam.exam_name}</strong>
                                  </td>
                                  <td>{exam.description || "—"}</td>
                                  <td>
                                    {exam.assessments?.length > 0
                                      ? `${exam.assessments.length} assessment${
                                          exam.assessments.length > 1 ? "s" : ""
                                        }`
                                      : "—"}
                                  </td>
                                  <td>{formatDate(exam.created_at)}</td>
                                  <td>
                                    <div className="d-flex align-items-center gap-2">
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="p-0"
                                        title="See Assessment Details"
                                        onClick={() => toggleExpand(exam.id)}
                                      >
                                        <i
                                          className={`mdi mdi-chevron-${
                                            expandedId === exam.id
                                              ? "up"
                                              : "down"
                                          }`}
                                          style={{ fontSize: "18px" }}
                                        />
                                      </Button>
                                      <Link
                                        to={`/exam-update/${exam.id}`}
                                        className="text-success"
                                        title="Edit"
                                      >
                                        <i className="mdi mdi-pencil font-size-18" />
                                      </Link>
                                      <Link
                                        to="#"
                                        className="text-danger"
                                        title="Delete"
                                        onClick={() => handleDelete(exam.id)}
                                        style={{
                                          pointerEvents:
                                            deletingId === exam.id
                                              ? "none"
                                              : "auto",
                                          opacity:
                                            deletingId === exam.id ? 0.5 : 1,
                                        }}
                                      >
                                        {deletingId === exam.id ? (
                                          <Spinner size="sm" color="danger" />
                                        ) : (
                                          <i className="mdi mdi-delete font-size-18" />
                                        )}
                                      </Link>
                                    </div>
                                  </td>
                                </tr>

                                {/* ── Expanded detail row ── */}
                                {expandedId === exam.id && (
                                  <tr>
                                    <td
                                      colSpan={6}
                                      style={{
                                        background: "#f8f9fc",
                                        padding: "16px 24px",
                                      }}
                                    >
                                      {exam.assessments &&
                                      exam.assessments.length > 0 ? (
                                        exam.assessments.map(
                                          (assessment, aIdx) => {
                                            const isCollapsed =
                                              collapsedAssessments.has(
                                                assessment.id
                                              )
                                            return (
                                              <div
                                                key={assessment.id}
                                                className={
                                                  aIdx > 0 ? "mt-3" : ""
                                                }
                                                style={{
                                                  border: "1px solid #e8eaf0",
                                                  borderRadius: "6px",
                                                  overflow: "hidden",
                                                }}
                                              >
                                                {/* Assessment header: collapse → status → name → marks */}
                                                <div
                                                  className="d-flex align-items-center gap-2 px-3 py-2"
                                                  style={{
                                                    background: "#f0f2f8",
                                                    borderBottom: isCollapsed
                                                      ? "none"
                                                      : "1px solid #e8eaf0",
                                                  }}
                                                >
                                                  {/* 1. Collapse toggle button */}
                                                  <Button
                                                    color="link"
                                                    size="sm"
                                                    className="p-0"
                                                    title={
                                                      isCollapsed
                                                        ? "Show subjects"
                                                        : "Hide subjects"
                                                    }
                                                    onClick={() =>
                                                      toggleAssessmentCollapse(
                                                        assessment.id
                                                      )
                                                    }
                                                  >
                                                    <i
                                                      className={`mdi mdi-chevron-${
                                                        isCollapsed
                                                          ? "down"
                                                          : "up"
                                                      }`}
                                                      style={{
                                                        fontSize: "18px",
                                                        color: "#556ee6",
                                                      }}
                                                    />
                                                  </Button>

                                                  {/* 2. Status dropdown */}
                                                  <StatusSelect
                                                    isActive={
                                                      !!assessment.is_active
                                                    }
                                                    isToggling={
                                                      togglingKey ===
                                                      `assessment-${assessment.id}`
                                                    }
                                                    onChange={newStatus =>
                                                      handleToggleAssessment(
                                                        exam.id,
                                                        assessment,
                                                        newStatus
                                                      )
                                                    }
                                                  />

                                                  {/* 3. Assessment name */}
                                                  <span
                                                    style={{
                                                      fontSize: "12px",
                                                      fontWeight: 700,
                                                      color: "#556ee6",
                                                      textTransform:
                                                        "uppercase",
                                                      letterSpacing: "0.5px",
                                                    }}
                                                  >
                                                    <i className="bx bx-layer me-1" />
                                                    {assessment.assessment_name}
                                                    {assessment.description && (
                                                      <span
                                                        className="ms-2 text-muted fw-normal"
                                                        style={{
                                                          textTransform: "none",
                                                          letterSpacing: 0,
                                                        }}
                                                      >
                                                        —{" "}
                                                        {assessment.description}
                                                      </span>
                                                    )}
                                                  </span>

                                                  {/* 4. Marks */}
                                                  {assessment.max_mark > 0 && (
                                                    <span
                                                      className="text-muted"
                                                      style={{
                                                        fontSize: "12px",
                                                      }}
                                                    >
                                                      (Marks:{" "}
                                                      {assessment.min_mark} –{" "}
                                                      {assessment.max_mark})
                                                    </span>
                                                  )}
                                                </div>

                                                {/* Collapsible subjects body */}
                                                {!isCollapsed && (
                                                  <div className="p-3">
                                                    {/* Direct subjects on assessment */}
                                                    {assessment.subjects &&
                                                      assessment.subjects
                                                        .length > 0 && (
                                                        <div className="table-responsive mb-2">
                                                          <table className="table table-sm table-bordered mb-0">
                                                            <thead className="table-light">
                                                              <tr>
                                                                <th width="40">
                                                                  #
                                                                </th>
                                                                <th>
                                                                  Subject -
                                                                  Topic
                                                                </th>
                                                                {!isCoAssessment(assessment.subjects) && (
                                                                  <th>
                                                                    Subject Code
                                                                  </th>
                                                                )}
                                                                <th>Classes</th>
                                                                <th>
                                                                  Min Mark
                                                                </th>
                                                                <th>
                                                                  Max Mark
                                                                </th>
                                                                <th width="110">
                                                                  Status
                                                                </th>
                                                              </tr>
                                                            </thead>
                                                            <tbody>
                                                              {assessment.subjects.map(
                                                                (sub, sIdx) => (
                                                                  <tr
                                                                    key={sub.id}
                                                                  >
                                                                    <td>
                                                                      {sIdx + 1}
                                                                    </td>
                                                                    <td>
                                                                      {
                                                                        sub.subject_name
                                                                      }
                                                                      {sub.topic_name && (
                                                                        <span
                                                                          className="text-muted ms-1"
                                                                          style={{
                                                                            fontSize:
                                                                              "11px",
                                                                          }}
                                                                        >
                                                                          —{" "}
                                                                          {
                                                                            sub.topic_name
                                                                          }
                                                                        </span>
                                                                      )}
                                                                    </td>
                                                                    {!isCoAssessment(assessment.subjects) && (
                                                                      <td>
                                                                        {sub.subject_code && String(sub.subject_code).startsWith("CO")
                                                                          ? ""
                                                                          : sub.subject_code}
                                                                      </td>
                                                                    )}
                                                                    <td>
                                                                      <div className="d-flex flex-wrap gap-1">
                                                                        {renderClassBadges(
                                                                          sub.st_class
                                                                        )}
                                                                      </div>
                                                                    </td>
                                                                    <td>
                                                                      {
                                                                        sub.min_mark
                                                                      }
                                                                    </td>
                                                                    <td>
                                                                      {
                                                                        sub.max_mark
                                                                      }
                                                                    </td>
                                                                    <td>
                                                                      <StatusSelect
                                                                        isActive={
                                                                          !!sub.is_active
                                                                        }
                                                                        disabled={
                                                                          !assessment.is_active
                                                                        }
                                                                        isToggling={
                                                                          togglingKey ===
                                                                          `subject-${sub.id}`
                                                                        }
                                                                        onChange={newStatus =>
                                                                          handleToggleSubject(
                                                                            exam.id,
                                                                            assessment.id,
                                                                            null,
                                                                            sub,
                                                                            newStatus
                                                                          )
                                                                        }
                                                                      />
                                                                    </td>
                                                                  </tr>
                                                                )
                                                              )}
                                                            </tbody>
                                                          </table>
                                                        </div>
                                                      )}

                                                    {/* Sub-assessments */}
                                                    {assessment.sub_assessments &&
                                                    assessment.sub_assessments
                                                      .length > 0
                                                      ? assessment.sub_assessments.map(
                                                          (sa, saIdx) => (
                                                            <div
                                                              key={sa.id}
                                                              className={`ms-3 ${
                                                                saIdx > 0
                                                                  ? "mt-3"
                                                                  : "mt-2"
                                                              }`}
                                                            >
                                                              <div
                                                                className="mb-1"
                                                                style={{
                                                                  fontSize:
                                                                    "11px",
                                                                  fontWeight: 700,
                                                                  color:
                                                                    "#f46a6a",
                                                                  textTransform:
                                                                    "uppercase",
                                                                  letterSpacing:
                                                                    "0.4px",
                                                                }}
                                                              >
                                                                <i className="bx bx-subdirectory-right me-1" />
                                                                {
                                                                  sa.sub_assessment_name
                                                                }
                                                              </div>

                                                              {sa.subjects &&
                                                              sa.subjects
                                                                .length > 0 ? (
                                                                <div className="table-responsive">
                                                                  <table className="table table-sm table-bordered mb-0">
                                                                    <thead className="table-light">
                                                                      <tr>
                                                                        <th width="40">
                                                                          #
                                                                        </th>
                                                                        <th>
                                                                          Subject
                                                                          -
                                                                          Topic
                                                                        </th>
                                                                        {!isCoAssessment(sa.subjects) && (
                                                                          <th>
                                                                            Subject
                                                                            Code
                                                                          </th>
                                                                        )}
                                                                        <th>
                                                                          Classes
                                                                        </th>
                                                                        <th>
                                                                          Min
                                                                          Mark
                                                                        </th>
                                                                        <th>
                                                                          Max
                                                                          Mark
                                                                        </th>
                                                                        <th width="110">
                                                                          Status
                                                                        </th>
                                                                      </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                      {sa.subjects.map(
                                                                        (
                                                                          sub,
                                                                          sIdx
                                                                        ) => (
                                                                          <tr
                                                                            key={
                                                                              sub.id
                                                                            }
                                                                          >
                                                                            <td>
                                                                              {sIdx +
                                                                                1}
                                                                            </td>
                                                                            <td>
                                                                              {
                                                                                sub.subject_name
                                                                              }
                                                                              {sub.topic_name && (
                                                                                <span
                                                                                  className="text-muted ms-1"
                                                                                  style={{
                                                                                    fontSize:
                                                                                      "11px",
                                                                                  }}
                                                                                >
                                                                                  —{" "}
                                                                                  {
                                                                                    sub.topic_name
                                                                                  }
                                                                                </span>
                                                                              )}
                                                                            </td>
                                                                            {!isCoAssessment(sa.subjects) && (
                                                                              <td>
                                                                                {sub.subject_code && String(sub.subject_code).startsWith("CO")
                                                                                  ? ""
                                                                                  : sub.subject_code}
                                                                              </td>
                                                                            )}
                                                                            <td>
                                                                              <div className="d-flex flex-wrap gap-1">
                                                                                {renderClassBadges(
                                                                                  sub.st_class
                                                                                )}
                                                                              </div>
                                                                            </td>
                                                                            <td>
                                                                              {
                                                                                sub.min_mark
                                                                              }
                                                                            </td>
                                                                            <td>
                                                                              {
                                                                                sub.max_mark
                                                                              }
                                                                            </td>
                                                                            <td>
                                                                              <StatusSelect
                                                                                isActive={
                                                                                  !!sub.is_active
                                                                                }
                                                                                disabled={
                                                                                  !assessment.is_active
                                                                                }
                                                                                isToggling={
                                                                                  togglingKey ===
                                                                                  `subject-${sub.id}`
                                                                                }
                                                                                onChange={newStatus =>
                                                                                  handleToggleSubject(
                                                                                    exam.id,
                                                                                    assessment.id,
                                                                                    sa.id,
                                                                                    sub,
                                                                                    newStatus
                                                                                  )
                                                                                }
                                                                              />
                                                                            </td>
                                                                          </tr>
                                                                        )
                                                                      )}
                                                                    </tbody>
                                                                  </table>
                                                                </div>
                                                              ) : (
                                                                <span
                                                                  className="text-muted ms-3"
                                                                  style={{
                                                                    fontSize:
                                                                      "12px",
                                                                  }}
                                                                >
                                                                  No subjects in
                                                                  this
                                                                  sub-assessment.
                                                                </span>
                                                              )}
                                                            </div>
                                                          )
                                                        )
                                                      : assessment.subjects
                                                          ?.length === 0 && (
                                                          <span
                                                            className="text-muted"
                                                            style={{
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            No subjects
                                                            configured.
                                                          </span>
                                                        )}
                                                  </div>
                                                )}
                                              </div>
                                            )
                                          }
                                        )
                                      ) : (
                                        <span className="text-muted">
                                          No assessments configured.
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {renderPagination()}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default ExamMain
